"use client";

import classNames from "classnames";
import * as React from "react";
import styled, { css } from "styled-components";

export interface CollapsableProps {
  children: React.ReactNode;
  /** When set to true, content has height 0. When this property changes, an animation is triggered */
  isCollapsed: boolean;
  /** the length of the animation */
  transitionDuration?: number;
  /** the delay before the animation starts */
  transitionDelay?: number;
  /** used to set a minimum height when collapsed to be something larger than zero. Can be used for "see more" types of content */
  closedHeight?: number;
  /** forwarded down to support adding additional styles, ie. styled(Collapsable)`...` */
  className?: string;
  /** The test id */
  "data-test-id"?: string;
}

/**
 * A container with an animation that will show and hide the content contained within it
 * @trackcomponent
 * @link [Docs](https://slate.faire.team/component/collapsable)
 */
export const Collapsable: React.FC<CollapsableProps> = ({
  isCollapsed,
  transitionDuration = 0.25,
  transitionDelay = 0,
  closedHeight = 0,
  ...props
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = React.useState(closedHeight);
  const isInitialTransition = useIsInitialValue(isCollapsed);

  const onTransition = React.useCallback(
    (event: Event) => {
      if (ref.current && event.target === ref.current) {
        if (event.type === "transitionstart") {
          ref.current.classList.add("transitioning");
        } else {
          ref.current.classList.remove("transitioning");
        }
      }
    },
    [ref]
  );

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (element) {
      element.addEventListener("transitionend", onTransition);
      element.addEventListener("transitioncancel", onTransition);
      element.addEventListener("transitionstart", onTransition);
      return () => {
        element.removeEventListener("transitionend", onTransition);
        element.removeEventListener("transitioncancel", onTransition);
        element.removeEventListener("transitionstart", onTransition);
      };
    } else {
      return undefined;
    }
  }, [onTransition, ref]);

  React.useLayoutEffect(() => {
    if (ref.current) {
      const computedStyle = getComputedStyle(ref.current);
      const height = ref.current.scrollHeight;
      const topPadding = parseFloat(computedStyle.paddingTop) || 0;
      const bottomPadding = parseFloat(computedStyle.paddingBottom) || 0;
      setMaxHeight(
        isCollapsed ? closedHeight : height + topPadding + bottomPadding
      );
    }
  }, [closedHeight, isCollapsed]);

  return (
    <CollapsableWrapper
      {...props}
      className={classNames(props.className, {
        /* Preemptively add class 'transitioning' to prevent flicker of
         * 'visibility: hidden' between when React finishes rendering,
         * and the 'transitionstart' handler is called.
         */
        transitioning: !isInitialTransition,
      })}
      $isCollapsed={isCollapsed}
      $transitionDuration={isInitialTransition ? 0 : transitionDuration}
      $transitionDelay={isInitialTransition ? 0 : transitionDelay}
      ref={ref}
      $maxHeight={`${maxHeight}px`}
    />
  );
};

const CollapsableWrapper = styled.div<{
  $isCollapsed: boolean;
  $transitionDuration: number;
  $transitionDelay: number;
  $maxHeight: string;
}>`
  transition: ${({ $transitionDuration, $transitionDelay }) =>
    css`max-height ${$transitionDuration}s ease ${$transitionDelay}s`};

  overflow: ${({ $isCollapsed }) => ($isCollapsed ? "hidden" : "visible")};
  visibility: ${({ $isCollapsed }) => ($isCollapsed ? "hidden" : "inherit")};
  max-height: ${({ $isCollapsed, $maxHeight }) =>
    $isCollapsed ? $maxHeight : "unset"};

  &.transitioning {
    overflow: hidden;
    visibility: inherit;
    max-height: ${({ $maxHeight }) => $maxHeight};
  }
`;

const useIsInitialValue = (propValue: boolean): boolean => {
  const [initialValue] = React.useState(propValue);
  const refHasChanged = React.useRef(false);
  if (initialValue !== propValue) {
    refHasChanged.current = true;
  }
  return !refHasChanged.current;
};
